import React, { useEffect, useState } from "react";
import { useBoxContext } from "../../../../Contexts/boxData.context.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { decodeFBX } from "../../../../utils/functions.ts";

import RichNoteEditor from "./noteEditor.component.jsx";
import RichNoteViewer from "./richNoteViewer.component.jsx";

export default function NoteDisplayPane({ height = "100%", letsDeleteNote = null, onEditorDirtyChange = null, onNoteSaved = null }) {
  const NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/notes.php";

  const { NoteBoxID, setNoteResetCode, setNoteBoxID, setNoteBoxOpen } = useBoxContext();
  const { activePlayer } = usePlayer();

  const [theNote, setTheNote] = useState(null);
  const [html, setHtml] = useState("");
  const [editable, setEditable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const normalizeHeight = (h) => {
    if (h == null) return "100%";
    if (typeof h === "number") return `${h}px`;
    return String(h);
  };

  const extractHtmlFromNote = (noteContent) => {
    let initialHtml = "";
    const comm = noteContent?.Communication;
    if (typeof comm === "string") initialHtml = comm;
    else if (comm && typeof comm.html === "string") initialHtml = comm.html;
    else if (typeof noteContent?.html === "string") initialHtml = noteContent.html;
    return initialHtml;
  };

  const fetchNote = async () => {
    if (!NoteBoxID) {
      setTheNote(null);
      setHtml("");
      setEditable(false);
      if (typeof onEditorDirtyChange === "function") onEditorDirtyChange(false);
      return;
    }

    setIsLoading(true);
    setEditable(false);
    if (typeof onEditorDirtyChange === "function") onEditorDirtyChange(false);

    try {
      const decoded = decodeFBX();
      if (!decoded) {
        setTheNote(null);
        setHtml("");
        return;
      }

      const [scout, team, security, displayName] = decoded.split(",");

      const response = await fetch(NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scout,
          team,
          scriptName: "getSingleNote",
          noteID: NoteBoxID,
        }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        console.error("getSingleNote failed:", response.status, errText);
        setTheNote(null);
        setHtml("");
        return;
      }

      const data = await response.json();
      const noteContent = data?.note || null;

      setTheNote(noteContent);

      const canEdit = displayName === noteContent?.ByName;
      setEditable(!!canEdit);

      setHtml(extractHtmlFromNote(noteContent));
    } catch (err) {
      console.error("Error fetching note:", err);
      setTheNote(null);
      setHtml("");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [NoteBoxID]);

  const letsSaveNote = async (updatedNote) => {
    try {
      const decoded = decodeFBX();
      const [scout, team] = decoded ? decoded.split(",") : [null, null];

      const response = await fetch(NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "saveNote",
          scout,
          team,
          note: updatedNote,
        }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        console.error("saveNote failed:", response.status, errText);
        alert(`Save failed (${response.status}). Check console.`);
        return;
      }

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      const rand = Math.floor(Math.random() * (100000 - 1000 + 1)) + 1000;
      setNoteResetCode(rand);

      setTheNote(null);
      setHtml("");
      setNoteBoxID(null);
      setNoteBoxOpen(false);

      if (typeof onEditorDirtyChange === "function") onEditorDirtyChange(false);
      if (typeof onNoteSaved === "function") onNoteSaved(updatedNote);

      if (data) console.log("saveNote ok:", data);
    } catch (err) {
      console.error("Error saving note:", err);
      alert("Save threw an error. Check console.");
    }
  };

  const handleEditorChange = (nextHtml) => setHtml(nextHtml);

  if (!activePlayer?.FBXID) {
    return (
      <div className="p-3 text-muted">
        <em>Select a player (left), then select a note (top-left list).</em>
      </div>
    );
  }

  if (!NoteBoxID) {
    return (
      <div className="p-3 text-muted">
        <em>There are no Notes for this player, you can create one by using the Create Button in the Top Right</em>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-3 alert-info">
        <strong>Loading note…</strong>
      </div>
    );
  }

  if (!theNote) {
    return (
      <div className="p-3 text-muted">
        <em>No note selected (or you don’t have access to view it).</em>
      </div>
    );
  }

  const h = normalizeHeight(height);

  return (
    <div className="w-100" style={{ height: h, overflow: "hidden" }}>
      <div className="border w-100 border-rounded" style={{ height: "100%", overflow: "hidden" }}>
        {editable ? (
          <div className="p-2" style={{ height: "100%", overflow: "hidden" }}>
            <RichNoteEditor
              value={html}
              onChange={handleEditorChange}
              noteData={theNote}
              letsSaveNote={letsSaveNote}
              letsDeleteNote={letsDeleteNote}
              onDirtyChange={onEditorDirtyChange}
              height="100%"
            />
          </div>
        ) : (
          <div className="p-2" style={{ height: "100%", overflow: "auto" }}>
            <RichNoteViewer html={html} noteData={theNote} />
          </div>
        )}
      </div>
    </div>
  );
}
